import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useAuth } from './AuthContext';
import axios from '../config/axiosConfig';
import {
  Container,
  CircularProgress,
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  Menu,
  MenuItem,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import useExchanges from '../hooks/useExchanges';
import TitleAndInfo from './TitleAndInfo';
import { formatNumber } from '../utils/numberUtils';
import { exportToCsv } from '../utils/exportToCsv';
import UnifiedFilterSystem from './UnifiedFilterSystem';
import currencySymbols from '../utils/currencySymbols';
import { KeyboardArrowDown as KeyboardArrowDownIcon } from '@mui/icons-material';
import { predefinedScreens } from '../config/predefinedScreens';

const UnifiedScreener = () => {
  const { user } = useAuth();
  const exchanges = useExchanges();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const baseUrl = window.location.origin;

  // Consolidated state management
  const [state, setState] = useState({
    isLoading: false,
    stocks: [],
    activeFilters: [],
    selectedScreenName: 'Popular Screens',
    currentTab: 0,
    popularScreensAnchor: null,
    hasInitialized: false
  });

  // Content configuration
  const titleTop = "Stock Screener";
  const title = "Complete Market Analysis Tool";
  const info = "Screen stocks using comprehensive criteria including price, moving averages, market cap, and company profile data. Filter stocks based on multiple metrics to find the perfect matches for your investment strategy.";

  // Centralized state updater
  const updateState = useCallback((updates) => {
    setState(prevState => ({ ...prevState, ...updates }));
  }, []);

  // Parse URL parameters to filter object
  const parseUrlParams = useCallback((searchParams) => {
    const filters = {};
    const params = new URLSearchParams(searchParams);

    for (const [key, value] of params.entries()) {
      if (value === '') continue;

      if (key === 'exchange') {
        filters[key] = value.split(',').map(v => decodeURIComponent(v.trim()));
      } else if (key === 'isEtf') {
        filters[key] = value === 'true';
      } else if (key.includes('MoreThan') || key.includes('LowerThan') ||
        key === 'minPercentage' || key === 'maxPercentage') {
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
          filters[key] = numValue;
        }
      } else {
        filters[key] = decodeURIComponent(value);
      }
    }

    return filters;
  }, []);

  // Convert filter object to URL parameters
  const filtersToUrlParams = useCallback((filterData) => {
    const params = new URLSearchParams();

    Object.entries(filterData).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;

      if (Array.isArray(value)) {
        if (value.length > 0) {
          params.set(key, value.map(v => encodeURIComponent(v)).join(','));
        }
      } else {
        params.set(key, encodeURIComponent(value.toString()));
      }
    });

    return params.toString();
  }, []);

  // Main search handler
  const handleSearch = useCallback(async (filterData, options = {}) => {
    const { screenName = null, skipUrlUpdate = false } = options;

    updateState({
      stocks: [],
      isLoading: true,
      selectedScreenName: screenName || state.selectedScreenName
    });

    try {
      // Update URL unless explicitly skipped
      if (!skipUrlUpdate) {
        const urlParams = filtersToUrlParams(filterData);
        const newUrl = urlParams ? `/screener?${urlParams}` : '/screener';
        navigate(newUrl, { replace: true });
      }

      // Make API call
      const response = await axios.post('/api/screener', filterData);
      updateState({
        stocks: response.data.data,
        isLoading: false
      });
    } catch (error) {
      console.error('Error fetching data:', error);
      updateState({ isLoading: false });
    }
  }, [navigate, state.selectedScreenName, updateState, filtersToUrlParams]);

  // Handle predefined screen selection
  const handlePredefinedScreen = useCallback((screen) => {
    updateState({ popularScreensAnchor: null });
    handleSearch(screen.filters, { screenName: screen.name });
  }, [handleSearch, updateState]);

  // Handle filter changes from UnifiedFilterSystem
  const handleFiltersChange = useCallback((filters) => {
    updateState({
      activeFilters: filters,
      // Reset screen name only when all filters are cleared manually
      ...(filters.length === 0 && { selectedScreenName: 'Popular Screens' })
    });
  }, [updateState]);

  // Handle clear action
  const handleClear = useCallback(() => {
    updateState({
      stocks: [],
      activeFilters: [],
      selectedScreenName: 'Popular Screens'
    });
    navigate('/screener', { replace: true });
  }, [navigate, updateState]);

  // Handle popular screens menu
  const handlePopularScreensClick = useCallback((event) => {
    updateState({ popularScreensAnchor: event.currentTarget });
  }, [updateState]);

  const handlePopularScreensClose = useCallback(() => {
    updateState({ popularScreensAnchor: null });
  }, [updateState]);

  // Handle tab change
  const handleTabChange = useCallback((event, newValue) => {
    updateState({ currentTab: newValue });
  }, [updateState]);

  // Initialize component
  useEffect(() => {
    if (!state.hasInitialized) {
      updateState({ hasInitialized: true });
    }
  }, [state.hasInitialized, updateState]);

  // Handle URL parameters and auto-search
  useEffect(() => {
    if (state.hasInitialized) {
      const urlFilters = parseUrlParams(location.search);

      // If URL has filters, trigger search automatically
      if (Object.keys(urlFilters).length > 0) {
        // Small delay to ensure components are ready
        const timer = setTimeout(() => {
          handleSearch(urlFilters, { skipUrlUpdate: true });
        }, 100);

        return () => clearTimeout(timer);
      }
    }
  }, [location.search, parseUrlParams, handleSearch, state.hasInitialized, updateState]);

  // Column definitions
  const baseColumns = useMemo(() => [
    {
      field: 'symbol',
      headerName: 'Company',
      minWidth: 200,
      flex: 1,
      renderCell: (params) => (
        <div>
          <RouterLink
            to={`${baseUrl}/asset/${params.row.symbol}`}
            style={{ color: theme.palette.primary.main, textDecoration: 'none', fontWeight: 'bold' }}
          >
            {params.row.symbol}
          </RouterLink>
          <div style={{ fontSize: '0.9em', color: '#888', fontWeight: 'bold' }}>{params.row.name}</div>
        </div>
      ),
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'price',
      headerName: 'Price',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const symbol = currencySymbols[params.row.currency] || '';
        return `${symbol}${formatNumber(params.value)}`;
      }
    },
    {
      field: 'changesPercentage',
      headerName: 'Change %',
      flex: 1,
      minWidth: 120,
      valueFormatter: (params) => formatNumber(params.value),
      renderCell: (params) => (
        <div style={{ color: params.value > 0 ? theme.palette.positive.main : params.value < 0 ? theme.palette.negative.main : theme.palette.lightGray.main }}>
          {formatNumber(params.value)}%
        </div>
      ),
    },
    {
      field: 'change',
      headerName: 'Change',
      flex: 1,
      minWidth: 120,
      valueFormatter: (params) => formatNumber(params.value),
      renderCell: (params) => (
        <div style={{ color: params.value > 0 ? theme.palette.positive.main : params.value < 0 ? theme.palette.negative.main : theme.palette.lightGray.main }}>
          {formatNumber(params.value)}
        </div>
      ),
    },
    { field: 'marketCap', headerName: 'Market Cap', minWidth: 130, flex: 1, valueFormatter: (params) => formatNumber(params.value) },
    { field: 'exchange', headerName: 'Exchange', minWidth: 120, flex: 1 },
    { field: 'volume', headerName: 'Volume', minWidth: 130, flex: 1, valueFormatter: (params) => formatNumber(params.value) },
  ], [theme, baseUrl]);

  const additionalColumns = useMemo(() => ({
    priceDetails: [
      { field: 'open', headerName: 'Open', minWidth: 100, flex: 1, valueFormatter: (params) => formatNumber(params.value) },
      { field: 'previousClose', headerName: 'Prev Close', minWidth: 120, flex: 1, valueFormatter: (params) => formatNumber(params.value) },
      { field: 'dayLow', headerName: 'Day Low', minWidth: 120, flex: 1, valueFormatter: (params) => formatNumber(params.value) },
      { field: 'dayHigh', headerName: 'Day High', minWidth: 120, flex: 1, valueFormatter: (params) => formatNumber(params.value) },
      { field: 'yearLow', headerName: 'Year Low', minWidth: 120, flex: 1, valueFormatter: (params) => formatNumber(params.value) },
      { field: 'yearHigh', headerName: 'Year High', minWidth: 120, flex: 1, valueFormatter: (params) => formatNumber(params.value) },
    ],
    technicals: [
      { field: 'rsi', headerName: 'RSI', minWidth: 100, flex: 1, valueFormatter: (params) => formatNumber(params.value) },
      { field: 'sma20', headerName: '20-Day SMA', minWidth: 130, flex: 1, valueFormatter: (params) => formatNumber(params.value) },
      { field: 'sma50', headerName: '50-Day SMA', minWidth: 130, flex: 1, valueFormatter: (params) => formatNumber(params.value) },
      { field: 'sma100', headerName: '100-Day SMA', minWidth: 130, flex: 1, valueFormatter: (params) => formatNumber(params.value) },
      { field: 'sma150', headerName: '150-Day SMA', minWidth: 130, flex: 1, valueFormatter: (params) => formatNumber(params.value) },
      { field: 'sma200', headerName: '200-Day SMA', minWidth: 130, flex: 1, valueFormatter: (params) => formatNumber(params.value) },
    ],
    fundamentals: [
      { field: 'eps', headerName: 'EPS', minWidth: 100, flex: 1, valueFormatter: (params) => formatNumber(params.value) },
      { field: 'pe', headerName: 'PE', minWidth: 100, flex: 1, valueFormatter: (params) => formatNumber(params.value) },
      { field: 'beta', headerName: 'Beta', minWidth: 100, flex: 1, valueFormatter: (params) => formatNumber(params.value) },
      { field: 'dcf', headerName: 'DCF', minWidth: 100, flex: 1, valueFormatter: (params) => formatNumber(params.value) },
      { field: 'dcfDiff', headerName: 'DCF Diff', minWidth: 100, flex: 1, valueFormatter: (params) => formatNumber(params.value) },
      { field: 'sharesOutstanding', headerName: 'Shares Out.', minWidth: 150, flex: 1, valueFormatter: (params) => formatNumber(params.value) },
      { field: 'earningsAnnouncement', headerName: 'Earnings Date', minWidth: 150, flex: 1, valueFormatter: (params) => params.value ? new Date(params.value).toLocaleDateString() : '' },
    ],
    other: [
      { field: 'sector', headerName: 'Sector', minWidth: 150, flex: 1 },
      { field: 'industry', headerName: 'Industry', minWidth: 150, flex: 1 },
      { field: 'country', headerName: 'Country', minWidth: 120, flex: 1 },
      { field: 'avgVolume', headerName: 'Avg Volume', minWidth: 130, flex: 1, valueFormatter: (params) => formatNumber(params.value) },
    ]
  }), []);

  // Get columns for filter tab based on active filters
  const getFilterColumns = useCallback(() => {
    const filterColumnIds = [];

    state.activeFilters.forEach(filter => {
      if (filter.id === 'averageComparison') {
        filterColumnIds.push('rsi', 'sma20', 'sma50', 'sma100', 'sma150', 'sma200');
      } else if (filter.id === 'rsi') {
        filterColumnIds.push('rsi');
      } else {
        const columnMap = {
          'sector': 'sector',
          'industry': 'industry',
          'country': 'country',
          'beta': 'beta',
          'eps': 'eps',
          'pe': 'pe',
          'dcf': 'dcf',
          'dcfDiff': 'dcfDiff',
          'avgVolume': 'avgVolume',
          'sharesOutstanding': 'sharesOutstanding',
          'dayLow': 'dayLow',
          'dayHigh': 'dayHigh',
          'yearLow': 'yearLow',
          'yearHigh': 'yearHigh',
          'open': 'open',
          'previousClose': 'previousClose',
          'isEtf': 'isEtf'
        };

        const columnId = columnMap[filter.id];
        if (columnId) {
          filterColumnIds.push(columnId);
        }
      }
    });

    const uniqueColumnIds = [...new Set(filterColumnIds)];
    const baseColumnIds = baseColumns.map(col => col.field);
    const additionalColumnIds = uniqueColumnIds.filter(id => !baseColumnIds.includes(id));

    const allAdditionalColumns = [
      ...additionalColumns.priceDetails,
      ...additionalColumns.technicals,
      ...additionalColumns.fundamentals,
      ...additionalColumns.other
    ];

    const additionalColumnsToShow = allAdditionalColumns.filter(col =>
      additionalColumnIds.includes(col.field)
    );

    return [...baseColumns, ...additionalColumnsToShow];
  }, [state.activeFilters, baseColumns, additionalColumns]);

  // Get columns based on current tab
  const getCurrentColumns = useCallback(() => {
    switch (state.currentTab) {
      case 0: // General
        return baseColumns;
      case 1: // Filters
        return getFilterColumns();
      case 2: // Price Details
        return [...baseColumns, ...additionalColumns.priceDetails];
      case 3: // Technicals
        return [...baseColumns, ...additionalColumns.technicals];
      case 4: // Fundamentals
        return [...baseColumns, ...additionalColumns.fundamentals];
      case 5: // All
        return [
          ...baseColumns,
          ...additionalColumns.priceDetails,
          ...additionalColumns.technicals,
          ...additionalColumns.fundamentals,
          ...additionalColumns.other
        ];
      default:
        return baseColumns;
    }
  }, [state.currentTab, baseColumns, additionalColumns, getFilterColumns]);

  // Handle export
  const handleExport = useCallback(() => {
    exportToCsv('stock_screener.csv', getCurrentColumns(), state.stocks);
  }, [state.stocks, getCurrentColumns]);

  // Tab configuration
  const tabs = useMemo(() => [
    { label: 'General', badge: null },
    { label: 'Filters', badge: state.activeFilters.length > 0 ? state.activeFilters.length : null },
    { label: 'Price Details', badge: null },
    { label: 'Technicals', badge: null },
    { label: 'Fundamentals', badge: null },
    { label: 'All', badge: null }
  ], [state.activeFilters.length]);

  // Get initial filters for UnifiedFilterSystem
  const initialFilters = useMemo(() => {
    return parseUrlParams(location.search);
  }, [location.search, parseUrlParams]);

  return (
    <Container maxWidth="xl" sx={{ mt: 4 }}>
      <TitleAndInfo titleTop={titleTop} title={title} info={info} />

      <UnifiedFilterSystem
        onSearch={(filterData) => handleSearch(filterData)}
        onClear={handleClear}
        onExport={handleExport}
        showExport={user.plan.name === 'Premium' && state.stocks.length > 0}
        exchanges={exchanges}
        initialFilters={initialFilters}
        onFiltersChange={handleFiltersChange}
        popularScreensButton={
          <Button
            size="small"
            onClick={handlePopularScreensClick}
            variant="contained"
            endIcon={<KeyboardArrowDownIcon />}
            sx={{ mr: 1 }}
          >
            {state.selectedScreenName}
          </Button>
        }
      />

      {/* Popular Screens Menu */}
      <Menu
        anchorEl={state.popularScreensAnchor}
        open={Boolean(state.popularScreensAnchor)}
        onClose={handlePopularScreensClose}
        PaperProps={{
          sx: {
            mt: 1,
            maxHeight: 400,
            width: 250,
            backgroundColor: theme.palette.background.paper,
            '& .MuiMenuItem-root': {
              py: 1,
            }
          }
        }}
      >
        {predefinedScreens.map((screen) => (
          <MenuItem
            key={screen.id}
            onClick={() => handlePredefinedScreen(screen)}
            sx={{
              color: theme.palette.text.primary,
              '&:hover': {
                bgcolor: theme.palette.action.hover
              }
            }}
          >
            {screen.name}
          </MenuItem>
        ))}
      </Menu>

      {/* Loading State */}
      {state.isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* No Results State */}
      {!state.isLoading && state.stocks.length === 0 && state.hasInitialized && (
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography>No results found. Adjust your filters or try a new search.</Typography>
        </Box>
      )}

      {/* Results Display */}
      {state.stocks.length > 0 && (
        <Container maxWidth="xl" sx={{ mt: 4 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs
              value={state.currentTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
            >
              {tabs.map((tab, index) => (
                <Tab
                  key={index}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {tab.label}
                      {tab.badge && (
                        <Box
                          sx={{
                            backgroundColor: theme.palette.primary.main,
                            color: 'white',
                            borderRadius: '50%',
                            minWidth: 20,
                            height: 20,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.75rem',
                            fontWeight: 'bold'
                          }}
                        >
                          {tab.badge}
                        </Box>
                      )}
                    </Box>
                  }
                  sx={{ textTransform: 'none' }}
                />
              ))}
            </Tabs>
          </Box>

          <DataGrid
            rows={state.stocks}
            columns={getCurrentColumns()}
            getRowId={(row) => row.symbol}
            disableColumnMenu
            pagination
            paginationPosition="middle"
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
              sorting: { sortModel: [{ field: 'marketCap', sort: 'desc' }] },
            }}
            pageSizeOptions={[5, 10, 25, 50]}
            sx={{
              '& .MuiDataGrid-cell': {
                fontSize: '1rem',
              },
              '& .MuiDataGrid-columnHeaders': {
                fontSize: '0.8rem',
              },
            }}
          />
        </Container>
      )}
    </Container>
  );
};

export default UnifiedScreener;